import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { X, Plus, Trash2, Edit2, Save, Search, Filter, Calendar, Download, ArrowUpDown, ArrowUp, ArrowDown } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function OperationalExpensesModal({ isOpen, onClose, branches, selectedBranch, period, customRange }) {
  const [expenses, setExpenses] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState(null);
  
  // NEW: Filter & Search States
  const [searchTerm, setSearchTerm] = useState('');
  const [filterCategory, setFilterCategory] = useState('all');
  const [filterPeriod, setFilterPeriod] = useState('this_month');
  const [customDateRange, setCustomDateRange] = useState({ start: '', end: '' });
  const [sortBy, setSortBy] = useState('date'); // date, amount
  const [sortOrder, setSortOrder] = useState('desc'); // asc, desc
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(10);
  
  const [formData, setFormData] = useState({
    date: new Date().toISOString().split('T')[0],
    category: 'service',
    amount: '',
    description: '',
    branch: selectedBranch !== 'all' ? selectedBranch : '',
    receipt_image: ''
  });
  
  const [imagePreview, setImagePreview] = useState(null);
  const [viewingReceipt, setViewingReceipt] = useState(null);

  const categories = [
    { value: 'service', label: 'Service & Maintenance' },
    { value: 'listrik', label: 'Listrik' },
    { value: 'internet', label: 'Internet' },
    { value: 'sewa', label: 'Sewa Tempat' },
    { value: 'gaji', label: 'Gaji Karyawan' },
    { value: 'transportasi', label: 'Transportasi' },
    { value: 'supplies', label: 'Perlengkapan' },
    { value: 'lainnya', label: 'Lainnya' }
  ];

  const token = localStorage.getItem('token');

  useEffect(() => {
    if (isOpen) {
      fetchExpenses();
    }
  }, [isOpen, filterPeriod, customDateRange, selectedBranch]);

  const fetchExpenses = async () => {
    setLoading(true);
    try {
      // Build date range based on filterPeriod
      let startDate, endDate;
      const now = new Date();
      
      if (filterPeriod === 'today') {
        const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
        startDate = today.toISOString().split('T')[0];
        endDate = today.toISOString().split('T')[0];
      } else if (filterPeriod === 'yesterday') {
        const yesterday = new Date(now);
        yesterday.setDate(yesterday.getDate() - 1);
        startDate = yesterday.toISOString().split('T')[0];
        endDate = yesterday.toISOString().split('T')[0];
      } else if (filterPeriod === 'this_week') {
        const currentDate = new Date(now);
        const dayOfWeek = currentDate.getDay();
        const weekStart = new Date(currentDate);
        weekStart.setDate(currentDate.getDate() - dayOfWeek);
        startDate = weekStart.toISOString().split('T')[0];
        endDate = now.toISOString().split('T')[0];
      } else if (filterPeriod === 'this_month') {
        startDate = new Date(now.getFullYear(), now.getMonth(), 1).toISOString().split('T')[0];
        endDate = new Date().toISOString().split('T')[0];
      } else if (filterPeriod === 'last_month') {
        const firstDayLastMonth = new Date(now.getFullYear(), now.getMonth() - 1, 1);
        startDate = firstDayLastMonth.toISOString().split('T')[0];
        const lastDayLastMonth = new Date(now.getFullYear(), now.getMonth(), 0);
        endDate = lastDayLastMonth.toISOString().split('T')[0];
      } else if (filterPeriod === 'custom' && customDateRange.start && customDateRange.end) {
        startDate = customDateRange.start;
        endDate = customDateRange.end;
      }

      const response = await axios.get(`${API_URL}/api/operational-expenses`, {
        headers: { 'Authorization': `Bearer ${token}` },
        params: {
          start_date: startDate,
          end_date: endDate,
          branch: selectedBranch !== 'all' ? selectedBranch : undefined
        }
      });

      setExpenses(response.data.expenses || []);
      setCurrentPage(1); // Reset to first page when data changes
    } catch (error) {
      console.error('Error fetching expenses:', error);
      toast.error('Gagal memuat data pengeluaran');
    } finally {
      setLoading(false);
    }
  };

  const handleImageUpload = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    // Validate file type
    if (!file.type.startsWith('image/')) {
      toast.error('File harus berupa gambar');
      return;
    }

    // Validate file size (max 2MB)
    if (file.size > 2 * 1024 * 1024) {
      toast.error('Ukuran file maksimal 2MB');
      return;
    }

    // Convert to base64
    const reader = new FileReader();
    reader.onloadend = () => {
      const base64String = reader.result;
      setFormData({ ...formData, receipt_image: base64String });
      setImagePreview(base64String);
    };
    reader.readAsDataURL(file);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.amount || parseFloat(formData.amount) <= 0) {
      toast.error('Jumlah harus lebih dari 0');
      return;
    }

    if (!formData.branch) {
      toast.error('Pilih cabang terlebih dahulu');
      return;
    }

    if (!formData.receipt_image && !editingId) {
      toast.error('Upload bukti pengeluaran (nota/struk) wajib!');
      return;
    }

    try {
      if (editingId) {
        // Update
        await axios.put(
          `${API_URL}/api/operational-expenses/${editingId}`,
          formData,
          { headers: { 'Authorization': `Bearer ${token}` } }
        );
        toast.success('Pengeluaran berhasil diupdate');
      } else {
        // Create
        await axios.post(
          `${API_URL}/api/operational-expenses`,
          formData,
          { headers: { 'Authorization': `Bearer ${token}` } }
        );
        toast.success('Pengeluaran berhasil ditambahkan');
      }

      // Reset form
      setFormData({
        date: new Date().toISOString().split('T')[0],
        category: 'service',
        amount: '',
        description: '',
        branch: selectedBranch !== 'all' ? selectedBranch : '',
        receipt_image: ''
      });
      setImagePreview(null);
      setShowForm(false);
      setEditingId(null);
      fetchExpenses();
    } catch (error) {
      console.error('Error saving expense:', error);
      toast.error(error.response?.data?.detail || 'Gagal menyimpan pengeluaran');
    }
  };

  const handleEdit = (expense) => {
    setFormData({
      date: expense.date,
      category: expense.category,
      amount: expense.amount.toString(),
      description: expense.description || '',
      branch: expense.branch,
      receipt_image: expense.receipt_image || ''
    });
    setImagePreview(expense.receipt_image || null);
    setEditingId(expense.id);
    setShowForm(true);
  };

  const handleDelete = async (expenseId) => {
    toast.warning('Yakin ingin menghapus pengeluaran ini?', {
      description: 'Pengeluaran yang dihapus tidak dapat dikembalikan',
      duration: 5000,
      action: {
        label: 'Hapus',
        onClick: async () => {
          try {
            await axios.delete(
              `${API_URL}/api/operational-expenses/${expenseId}`,
              { headers: { 'Authorization': `Bearer ${token}` } }
            );
            toast.success('Pengeluaran berhasil dihapus');
            fetchExpenses();
          } catch (error) {
            console.error('Error deleting expense:', error);
            toast.error('Gagal menghapus pengeluaran');
          }
        }
      }
    });
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('id-ID', {
      style: 'currency',
      currency: 'IDR',
      minimumFractionDigits: 0
    }).format(amount);
  };

  const formatDate = (dateStr) => {
    return new Date(dateStr).toLocaleDateString('id-ID', {
      day: 'numeric',
      month: 'short',
      year: 'numeric'
    });
  };

  const getCategoryLabel = (value) => {
    return categories.find(c => c.value === value)?.label || value;
  };

  // Filter & Search Logic
  const getFilteredExpenses = () => {
    let filtered = [...expenses];

    // Search filter
    if (searchTerm) {
      filtered = filtered.filter(exp =>
        getCategoryLabel(exp.category).toLowerCase().includes(searchTerm.toLowerCase()) ||
        (exp.description && exp.description.toLowerCase().includes(searchTerm.toLowerCase())) ||
        exp.amount.toString().includes(searchTerm) ||
        exp.branch.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }

    // Category filter
    if (filterCategory !== 'all') {
      filtered = filtered.filter(exp => exp.category === filterCategory);
    }

    // Sort
    filtered.sort((a, b) => {
      if (sortBy === 'date') {
        const dateA = new Date(a.date);
        const dateB = new Date(b.date);
        return sortOrder === 'asc' ? dateA - dateB : dateB - dateA;
      } else if (sortBy === 'amount') {
        return sortOrder === 'asc' ? a.amount - b.amount : b.amount - a.amount;
      }
      return 0;
    });

    return filtered;
  };

  const filteredExpenses = getFilteredExpenses();
  const totalExpenses = filteredExpenses.reduce((sum, exp) => sum + exp.amount, 0);

  // Pagination
  const totalPages = Math.ceil(filteredExpenses.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const paginatedExpenses = filteredExpenses.slice(startIndex, endIndex);

  // Summary by category
  const summaryByCategory = categories.map(cat => {
    const categoryExpenses = filteredExpenses.filter(exp => exp.category === cat.value);
    const total = categoryExpenses.reduce((sum, exp) => sum + exp.amount, 0);
    return {
      category: cat.label,
      total,
      count: categoryExpenses.length
    };
  }).filter(s => s.count > 0);

  // Export to CSV
  const exportToCSV = () => {
    const headers = ['Tanggal', 'Kategori', 'Jumlah', 'Cabang', 'Keterangan', 'Dibuat Oleh'];
    const rows = filteredExpenses.map(exp => [
      formatDate(exp.date),
      getCategoryLabel(exp.category),
      exp.amount,
      exp.branch,
      exp.description || '-',
      exp.created_by
    ]);

    const csvContent = [
      headers.join(','),
      ...rows.map(row => row.map(cell => `"${cell}"`).join(','))
    ].join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = `pengeluaran_${new Date().toISOString().split('T')[0]}.csv`;
    link.click();
    toast.success('Data berhasil di-export!');
  };

  const toggleSort = (field) => {
    if (sortBy === field) {
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
    } else {
      setSortBy(field);
      setSortOrder('desc');
    }
  };

  const getPeriodLabel = () => {
    const labels = {
      'today': 'Hari Ini',
      'yesterday': 'Kemarin',
      'this_week': 'Minggu Ini',
      'this_month': 'Bulan Ini',
      'last_month': 'Bulan Lalu',
      'custom': `${customDateRange.start || ''} s/d ${customDateRange.end || ''}`
    };
    return labels[filterPeriod] || 'Semua';
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-4xl max-h-[90vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="p-6 border-b bg-gradient-to-r from-orange-500 to-red-500 text-white">
          <div className="flex items-center justify-between">
            <h2 className="text-2xl font-bold">Pengeluaran Operasional</h2>
            <button
              onClick={onClose}
              className="p-2 hover:bg-white/20 rounded-lg transition-colors"
            >
              <X className="h-6 w-6" />
            </button>
          </div>
          <p className="text-white/90 mt-1 text-sm">Kelola pengeluaran operasional bisnis</p>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6">
          {/* Top Actions Bar */}
          <div className="flex flex-wrap items-center justify-between gap-3 mb-6">
            <button
              onClick={() => setShowForm(true)}
              disabled={showForm}
              className="flex items-center gap-2 px-4 py-2 bg-orange-500 text-white rounded-lg hover:bg-orange-600 transition-colors disabled:opacity-50"
            >
              <Plus className="h-5 w-5" />
              Tambah Pengeluaran
            </button>
            <button
              onClick={exportToCSV}
              className="flex items-center gap-2 px-4 py-2 bg-green-500 text-white rounded-lg hover:bg-green-600 transition-colors"
            >
              <Download className="h-5 w-5" />
              Export CSV
            </button>
          </div>

          {/* Filters Section */}
          <div className="mb-6 p-4 bg-gray-50 rounded-xl border space-y-4">
            <div className="flex items-center gap-2 mb-2">
              <Filter className="h-4 w-4 text-gray-600" />
              <h3 className="font-semibold text-gray-700">Filter & Pencarian</h3>
            </div>

            {/* Period Filter */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">
                  <Calendar className="h-3 w-3 inline mr-1" />
                  Periode
                </label>
                <select
                  value={filterPeriod}
                  onChange={(e) => {
                    setFilterPeriod(e.target.value);
                    if (e.target.value !== 'custom') {
                      setCustomDateRange({ start: '', end: '' });
                    }
                  }}
                  className="w-full px-3 py-2 text-sm border rounded-lg focus:ring-2 focus:ring-orange-500"
                >
                  <option value="today">Hari Ini</option>
                  <option value="yesterday">Kemarin</option>
                  <option value="this_week">Minggu Ini</option>
                  <option value="this_month">Bulan Ini</option>
                  <option value="last_month">Bulan Lalu</option>
                  <option value="custom">Custom Range</option>
                </select>
              </div>

              {/* Category Filter */}
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">Kategori</label>
                <select
                  value={filterCategory}
                  onChange={(e) => {
                    setFilterCategory(e.target.value);
                    setCurrentPage(1);
                  }}
                  className="w-full px-3 py-2 text-sm border rounded-lg focus:ring-2 focus:ring-orange-500"
                >
                  <option value="all">Semua Kategori</option>
                  {categories.map(cat => (
                    <option key={cat.value} value={cat.value}>{cat.label}</option>
                  ))}
                </select>
              </div>

              {/* Search */}
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">
                  <Search className="h-3 w-3 inline mr-1" />
                  Cari
                </label>
                <input
                  type="text"
                  placeholder="Cari kategori, deskripsi, jumlah..."
                  value={searchTerm}
                  onChange={(e) => {
                    setSearchTerm(e.target.value);
                    setCurrentPage(1);
                  }}
                  className="w-full px-3 py-2 text-sm border rounded-lg focus:ring-2 focus:ring-orange-500"
                />
              </div>
            </div>

            {/* Custom Date Range */}
            {filterPeriod === 'custom' && (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3 pt-3 border-t">
                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1">Dari Tanggal</label>
                  <input
                    type="date"
                    value={customDateRange.start}
                    onChange={(e) => setCustomDateRange({ ...customDateRange, start: e.target.value })}
                    className="w-full px-3 py-2 text-sm border rounded-lg focus:ring-2 focus:ring-orange-500"
                  />
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1">Sampai Tanggal</label>
                  <input
                    type="date"
                    value={customDateRange.end}
                    onChange={(e) => setCustomDateRange({ ...customDateRange, end: e.target.value })}
                    className="w-full px-3 py-2 text-sm border rounded-lg focus:ring-2 focus:ring-orange-500"
                  />
                </div>
              </div>
            )}

            {/* Active Filter Info */}
            <div className="flex items-center gap-2 text-xs text-gray-600 pt-2 border-t">
              <span className="font-semibold">Periode Aktif:</span>
              <span className="bg-orange-100 px-2 py-1 rounded">{getPeriodLabel()}</span>
              {searchTerm && (
                <span className="bg-blue-100 px-2 py-1 rounded">
                  Pencarian: "{searchTerm}"
                </span>
              )}
              {filterCategory !== 'all' && (
                <span className="bg-purple-100 px-2 py-1 rounded">
                  Kategori: {getCategoryLabel(filterCategory)}
                </span>
              )}
            </div>
          </div>

          {/* Form */}
          {showForm && (
            <form onSubmit={handleSubmit} className="mb-6 p-4 bg-gray-50 rounded-xl border">
              <h3 className="font-semibold mb-4">
                {editingId ? 'Edit Pengeluaran' : 'Tambah Pengeluaran Baru'}
              </h3>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Tanggal
                  </label>
                  <input
                    type="date"
                    value={formData.date}
                    onChange={(e) => setFormData({ ...formData, date: e.target.value })}
                    className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                    required
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Kategori
                  </label>
                  <select
                    value={formData.category}
                    onChange={(e) => setFormData({ ...formData, category: e.target.value })}
                    className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                    required
                  >
                    {categories.map(cat => (
                      <option key={cat.value} value={cat.value}>{cat.label}</option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Jumlah (Rp)
                  </label>
                  <input
                    type="number"
                    value={formData.amount}
                    onChange={(e) => setFormData({ ...formData, amount: e.target.value })}
                    placeholder="0"
                    className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                    required
                    min="0"
                    step="1000"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Cabang
                  </label>
                  <select
                    value={formData.branch}
                    onChange={(e) => setFormData({ ...formData, branch: e.target.value })}
                    className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                    required
                  >
                    <option value="">Pilih Cabang</option>
                    {branches.map(branch => (
                      <option key={branch.id} value={branch.name}>{branch.name}</option>
                    ))}
                  </select>
                </div>

                <div className="md:col-span-2">
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Keterangan (Opsional)
                  </label>
                  <textarea
                    value={formData.description}
                    onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                    placeholder="Deskripsi pengeluaran..."
                    rows="2"
                    className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                  />
                </div>

                <div className="md:col-span-2">
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Upload Bukti Pengeluaran (Nota/Struk) <span className="text-red-500">*WAJIB</span>
                  </label>
                  <input
                    type="file"
                    accept="image/*"
                    onChange={handleImageUpload}
                    className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                    required={!editingId && !formData.receipt_image}
                  />
                  {imagePreview && (
                    <div className="mt-2">
                      <p className="text-xs text-gray-600 mb-1">Preview:</p>
                      <img 
                        src={imagePreview} 
                        alt="Receipt preview" 
                        className="max-h-48 rounded-lg border cursor-pointer hover:opacity-75"
                        onClick={() => setViewingReceipt(imagePreview)}
                      />
                    </div>
                  )}
                </div>
              </div>

              <div className="flex gap-2 mt-4">
                <button
                  type="submit"
                  className="flex items-center gap-2 px-4 py-2 bg-green-500 text-white rounded-lg hover:bg-green-600 transition-colors"
                >
                  <Save className="h-4 w-4" />
                  {editingId ? 'Update' : 'Simpan'}
                </button>
                <button
                  type="button"
                  onClick={() => {
                    setShowForm(false);
                    setEditingId(null);
                    setImagePreview(null);
                    setFormData({
                      date: new Date().toISOString().split('T')[0],
                      category: 'service',
                      amount: '',
                      description: '',
                      branch: selectedBranch !== 'all' ? selectedBranch : ''
                    });
                  }}
                  className="px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 transition-colors"
                >
                  Batal
                </button>
              </div>
            </form>
          )}

          {/* Summary Cards */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
            {/* Total Summary */}
            <div className="p-4 bg-gradient-to-br from-orange-50 to-red-50 border border-orange-200 rounded-xl">
              <div className="flex justify-between items-center">
                <span className="font-semibold text-gray-700">Total Pengeluaran</span>
                <span className="text-2xl font-bold text-orange-600">{formatCurrency(totalExpenses)}</span>
              </div>
              <p className="text-sm text-gray-600 mt-1">{filteredExpenses.length} dari {expenses.length} transaksi</p>
            </div>

            {/* Category Breakdown */}
            <div className="p-4 bg-gradient-to-br from-blue-50 to-purple-50 border border-blue-200 rounded-xl">
              <p className="font-semibold text-gray-700 mb-2">Breakdown Kategori</p>
              <div className="space-y-1 max-h-20 overflow-y-auto text-xs">
                {summaryByCategory.length > 0 ? (
                  summaryByCategory.map((item, idx) => (
                    <div key={idx} className="flex justify-between items-center">
                      <span className="text-gray-600">{item.category} ({item.count})</span>
                      <span className="font-semibold text-gray-800">{formatCurrency(item.total)}</span>
                    </div>
                  ))
                ) : (
                  <p className="text-gray-500 text-center py-2">Tidak ada data</p>
                )}
              </div>
            </div>
          </div>

          {/* Sort Controls */}
          <div className="flex items-center gap-2 mb-3">
            <span className="text-sm text-gray-600">Urutkan:</span>
            <button
              onClick={() => toggleSort('date')}
              className={`flex items-center gap-1 px-3 py-1 text-sm rounded-lg transition-colors ${
                sortBy === 'date' ? 'bg-orange-100 text-orange-700' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
            >
              Tanggal
              {sortBy === 'date' && (
                sortOrder === 'asc' ? <ArrowUp className="h-3 w-3" /> : <ArrowDown className="h-3 w-3" />
              )}
            </button>
            <button
              onClick={() => toggleSort('amount')}
              className={`flex items-center gap-1 px-3 py-1 text-sm rounded-lg transition-colors ${
                sortBy === 'amount' ? 'bg-orange-100 text-orange-700' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
            >
              Jumlah
              {sortBy === 'amount' && (
                sortOrder === 'asc' ? <ArrowUp className="h-3 w-3" /> : <ArrowDown className="h-3 w-3" />
              )}
            </button>
          </div>

          {/* Expenses List */}
          {loading ? (
            <div className="text-center py-8">
              <div className="inline-block animate-spin rounded-full h-8 w-8 border-4 border-orange-500 border-t-transparent"></div>
              <p className="text-gray-600 mt-2">Memuat data...</p>
            </div>
          ) : filteredExpenses.length === 0 ? (
            <div className="text-center py-8 text-gray-500">
              <p>Tidak ada pengeluaran yang sesuai filter</p>
              {searchTerm && (
                <button
                  onClick={() => {
                    setSearchTerm('');
                    setFilterCategory('all');
                  }}
                  className="mt-2 text-sm text-orange-600 hover:underline"
                >
                  Reset Filter
                </button>
              )}
            </div>
          ) : (
            <>
              <div className="space-y-2">
                {paginatedExpenses.map(expense => (
                <div
                  key={expense.id}
                  className="p-4 border rounded-lg hover:bg-gray-50 transition-colors"
                >
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-1">
                        <span className="font-semibold text-gray-900">
                          {getCategoryLabel(expense.category)}
                        </span>
                        <span className="text-xs bg-gray-200 px-2 py-1 rounded-full">
                          {expense.branch}
                        </span>
                      </div>
                      <p className="text-sm text-gray-600">{formatDate(expense.date)}</p>
                      {expense.description && (
                        <p className="text-sm text-gray-500 mt-1">{expense.description}</p>
                      )}
                      <p className="text-xs text-gray-400 mt-1">
                        Dibuat oleh: {expense.created_by}
                      </p>
                      {expense.receipt_image && (
                        <button
                          onClick={() => setViewingReceipt(expense.receipt_image)}
                          className="text-xs text-blue-600 hover:underline mt-1"
                        >
                           Lihat Bukti
                        </button>
                      )}
                    </div>
                    <div className="flex items-center gap-2 ml-4">
                      <span className="text-lg font-bold text-orange-600">
                        {formatCurrency(expense.amount)}
                      </span>
                      <button
                        onClick={() => handleEdit(expense)}
                        className="p-2 text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
                        title="Edit"
                      >
                        <Edit2 className="h-4 w-4" />
                      </button>
                      <button
                        onClick={() => handleDelete(expense.id)}
                        className="p-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                        title="Hapus"
                      >
                        <Trash2 className="h-4 w-4" />
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>

            {/* Pagination */}
            {totalPages > 1 && (
              <div className="mt-4 flex items-center justify-between pt-4 border-t">
                <div className="text-sm text-gray-600">
                  Menampilkan {startIndex + 1}-{Math.min(endIndex, filteredExpenses.length)} dari {filteredExpenses.length} transaksi
                </div>
                <div className="flex items-center gap-2">
                  <button
                    onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
                    disabled={currentPage === 1}
                    className="px-3 py-1 text-sm bg-gray-200 text-gray-700 rounded hover:bg-gray-300 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    Prev
                  </button>
                  <span className="text-sm text-gray-600">
                    Hal {currentPage} dari {totalPages}
                  </span>
                  <button
                    onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
                    disabled={currentPage === totalPages}
                    className="px-3 py-1 text-sm bg-gray-200 text-gray-700 rounded hover:bg-gray-300 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    Next
                  </button>
                </div>
              </div>
            )}
          </>
          )}
        </div>

        {/* Footer */}
        <div className="p-4 border-t bg-gray-50">
          <button
            onClick={onClose}
            className="w-full px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 transition-colors font-medium"
          >
            Tutup
          </button>
        </div>
      </div>

      {/* Image Viewer Modal */}
      {viewingReceipt && (
        <div 
          className="fixed inset-0 bg-black/80 z-[60] flex items-center justify-center p-4"
          onClick={() => setViewingReceipt(null)}
        >
          <div className="relative max-w-4xl max-h-[90vh]">
            <button
              onClick={() => setViewingReceipt(null)}
              className="absolute -top-10 right-0 text-white hover:text-gray-300"
            >
              <X className="h-8 w-8" />
            </button>
            <img 
              src={viewingReceipt} 
              alt="Receipt" 
              className="max-w-full max-h-[90vh] rounded-lg"
              onClick={(e) => e.stopPropagation()}
            />
          </div>
        </div>
      )}
    </div>
  );
}
